import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Checkbox } from "../ui/checkbox";
import { Separator } from "../ui/separator";
import { CheckCircle, Building, Info, Clock, CalendarIcon, IndianRupee, Plus, Eye } from "lucide-react";
import { format } from "date-fns";
import { cn } from "../../lib/utils";
import { formatDisplayDate } from "../../lib/dateFormat";
import { ScheduleLine } from "../../types/schema";
import {
  buildInterimScheduleLineFromPreview,
  previewInterimFromSchedule,
  resolveTdsRatePercent,
} from "../../lib/calculations";
import { useCustomerWorkflow } from "../../context/CustomerWorkflowContext";
import { CustomerAddressManagementDialog } from "../customers/CustomerAddressManagementDialog";
import { fetchCustomerAddresses, toBillToAddress } from "../../lib/customer-addresses";
import { CustomerAddress } from "../../types/supplier";

export interface Address {
  id: string;
  label: string;
  address: string;
  gstin?: string;
  stateId?: string | number;
}

export type AssignmentMode = "now" | "later";

export type ApproveScheduleConfirmPayload = {
  address: Address;
  assignmentMode: AssignmentMode;
  /** yyyy-MM-dd — required when assignmentMode is "later" */
  fundingDate?: string;
  interimLine?: ScheduleLine | null;
};

interface ApproveScheduleModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerName: string;
  onConfirm: (payload: ApproveScheduleConfirmPayload) => void | Promise<void>;
  customerAddresses: Address[] | null;
  scheduleLines?: ScheduleLine[];
  /** TDS % from schedule config (e.g. 2, 10). */
  tdsRatePercent?: number;
  customerId?: string;
  onAddressesChange?: (addresses: Address[]) => void;
}

const truncateAddress = (address: string, maxLength: number = 50): string => {
  if (!address) return "";
  if (address.length <= maxLength) return address;
  return `${address.substring(0, maxLength).trim()}...`;
};

export default function ApproveScheduleModal({
  open,
  onOpenChange,
  customerName,
  onConfirm,
  customerAddresses,
  scheduleLines = [],
  tdsRatePercent: tdsRatePercentProp,
  customerId,
  onAddressesChange,
}: ApproveScheduleModalProps) {
  const { refreshWorkflow } = useCustomerWorkflow();
  const [billToId, setBillToId] = useState("");
  const [assignmentMode, setAssignmentMode] = useState<AssignmentMode>("now");
  const [fundingDate, setFundingDate] = useState("");
  const [interimEnabled, setInterimEnabled] = useState(false);
  const [overrideInterimBase, setOverrideInterimBase] = useState(false);
  const [interimBaseOverrideAmount, setInterimBaseOverrideAmount] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [addAddressOpen, setAddAddressOpen] = useState(false);
  const [viewAddressOpen, setViewAddressOpen] = useState(false);
  const [billToAddresses, setBillToAddresses] = useState<Address[]>(
    customerAddresses ?? []
  );

  useEffect(() => {
    if (!open) return;
    setBillToAddresses(customerAddresses ?? []);
    if (!customerId) return;
    let cancelled = false;
    fetchCustomerAddresses(customerId)
      .then((list) => {
        if (cancelled) return;
        const mapped = list.map(toBillToAddress);
        setBillToAddresses(mapped);
        onAddressesChange?.(mapped);
      })
      .catch(() => {
        if (!cancelled) setBillToAddresses(customerAddresses ?? []);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, customerId]);

  const gstinAddresses = useMemo(
    () => billToAddresses.filter((addr) => !!addr.gstin),
    [billToAddresses]
  );

  const selectedBillTo = gstinAddresses.find((a) => a.id === billToId);

  const resolvedTdsRatePercent = useMemo(
    () => resolveTdsRatePercent(scheduleLines, tdsRatePercentProp),
    [scheduleLines, tdsRatePercentProp]
  );

  const interimPreview = useMemo(() => {
    if (assignmentMode !== "later" || !fundingDate || scheduleLines.length === 0) {
      return null;
    }
    return previewInterimFromSchedule(
      scheduleLines,
      fundingDate,
      resolvedTdsRatePercent
    );
  }, [assignmentMode, fundingDate, scheduleLines, resolvedTdsRatePercent]);

  const effectiveInterimBase = useMemo(() => {
    if (!interimPreview) return 0;
    if (overrideInterimBase) {
      return Math.ceil(parseFloat(interimBaseOverrideAmount) || 0);
    }
    return interimPreview.baseRentRounded;
  }, [interimPreview, overrideInterimBase, interimBaseOverrideAmount]);

  useEffect(() => {
    if (!open) {
      setBillToId("");
      setAssignmentMode("now");
      setFundingDate("");
      setInterimEnabled(false);
      setOverrideInterimBase(false);
      setInterimBaseOverrideAmount("");
      setIsSubmitting(false);
      setAddAddressOpen(false);
      setViewAddressOpen(false);
    }
  }, [open]);

  useEffect(() => {
    setInterimEnabled(false);
    setOverrideInterimBase(false);
    setInterimBaseOverrideAmount("");
  }, [fundingDate]);

  const buildInterimLine = (): ScheduleLine | null => {
    if (!interimEnabled || !interimPreview) return null;
    const baseOverride = overrideInterimBase
      ? parseFloat(interimBaseOverrideAmount) || 0
      : undefined;
    return buildInterimScheduleLineFromPreview(
      interimPreview,
      baseOverride,
      resolvedTdsRatePercent
    );
  };

  const handleConfirm = async () => {
    if (!selectedBillTo || isSubmitting) return;

    const payload: ApproveScheduleConfirmPayload = {
      address: selectedBillTo,
      assignmentMode,
    };

    if (assignmentMode === "later") {
      if (!fundingDate) return;
      payload.fundingDate = fundingDate;
      payload.interimLine = buildInterimLine();
    }

    setIsSubmitting(true);
    try {
      await onConfirm(payload);
      onOpenChange(false);
    } catch {
      // Parent shows error toast; keep modal open for retry
    } finally {
      setIsSubmitting(false);
      await refreshWorkflow();
    }
  };

  const handleAddressDialogChange = (nextOpen: boolean) => {
    setViewAddressOpen(nextOpen);
    setAddAddressOpen(nextOpen);
  };

  const handleAddressesUpdated = (list: CustomerAddress[]) => {
    const mapped = list.map(toBillToAddress);
    setBillToAddresses(mapped);
    onAddressesChange?.(mapped);
    if (billToId && !mapped.some((addr) => addr.id === billToId)) {
      setBillToId("");
    }
  };

  const isValid =
    Boolean(billToId) &&
    (assignmentMode === "now" || Boolean(fundingDate));

  return (
    <>
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (viewAddressOpen || addAddressOpen) return;
        onOpenChange(next);
      }}
    >
      <DialogContent className="sm:max-w-[560px] bg-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-primary" />
            Approve Rental Schedule
          </DialogTitle>
          <DialogDescription>
            Confirm billing address & lender assignment for{" "}
            <span className="font-medium text-foreground">{customerName}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4 max-h-[70vh] overflow-y-auto">
          {/* Lender Assignment */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Lender Assignment</Label>
            <RadioGroup
              value={assignmentMode}
              onValueChange={(v) => setAssignmentMode(v as AssignmentMode)}
              className="grid grid-cols-2 gap-3"
            >
              <label
                htmlFor="assign-now"
                className={cn(
                  "flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors",
                  assignmentMode === "now"
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-muted-foreground/30"
                )}
              >
                <RadioGroupItem value="now" id="assign-now" className="mt-0.5" />
                <div className="space-y-1">
                  <span className="text-sm font-medium">Assign Now</span>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Assign lender via NoA. Invoices created on NoA.
                  </p>
                </div>
              </label>
              <label
                htmlFor="assign-later"
                className={cn(
                  "flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors",
                  assignmentMode === "later"
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-muted-foreground/30"
                )}
              >
                <RadioGroupItem value="later" id="assign-later" className="mt-0.5" />
                <div className="space-y-1">
                  <span className="text-sm font-medium">Assign Later</span>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Self-funded invoices generated now. Assign lender later via NoA.
                  </p>
                </div>
              </label>
            </RadioGroup>

            {assignmentMode === "later" && (
              <div className="space-y-3 pt-1">
                <div className="flex items-start gap-2 p-2.5 rounded-md bg-blue-50 border border-blue-200">
                  <Info className="h-3.5 w-3.5 text-blue-600 mt-0.5 shrink-0" />
                  <p className="text-xs text-blue-700 leading-relaxed">
                    Rental invoices generated as self-funded (payee: Astrafin). Optionally enable interim
                    rental from the funding date to the first due date.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium flex items-center gap-2">
                    <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                    Funding Date
                  </Label>
                  <Input
                    type="date"
                    value={fundingDate}
                    onChange={(e) => setFundingDate(e.target.value)}
                    className="w-full"
                  />
                </div>

                {fundingDate && (
                  <div className="rounded-md border bg-muted/40 p-3 text-sm space-y-1.5">
                    {interimPreview ? (
                      <>
                        <div className="flex items-center gap-2">
                          <Checkbox
                            id="enable-interim"
                            checked={interimEnabled}
                            onCheckedChange={(checked) => {
                              const on = checked === true;
                              setInterimEnabled(on);
                              if (!on) {
                                setOverrideInterimBase(false);
                                setInterimBaseOverrideAmount("");
                              }
                            }}
                          />
                          <Label
                            htmlFor="enable-interim"
                            className="text-sm font-medium cursor-pointer flex items-center gap-2"
                          >
                            <Clock className="h-4 w-4 text-primary" />
                            Enable interim rental
                          </Label>
                        </div>
                        {interimEnabled && (
                      <div className="space-y-3 pt-1">
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-muted-foreground">
                          <div>From</div>
                          <div className="text-foreground text-right">
                            {formatDisplayDate(fundingDate)}
                          </div>
                          <div>To</div>
                          <div className="text-foreground text-right">
                            {format(
                              new Date(new Date(interimPreview.firstDueDate).getTime() - 86400000),
                              "dd MMM yyyy"
                            )}
                          </div>
                          <div>Interim Days</div>
                          <div className="text-foreground text-right">
                            {interimPreview.interimDays} / {interimPreview.regularDays}
                          </div>
                          <div>Interim Base (auto)</div>
                          <div className="text-foreground text-right font-medium">
                            ₹{interimPreview.baseRentRounded.toLocaleString("en-IN")}
                          </div>
                        </div>

                        <Separator />

                        <div className="flex items-center gap-2">
                          <Checkbox
                            id="override-interim-base"
                            checked={overrideInterimBase}
                            onCheckedChange={(checked) => {
                              const on = checked === true;
                              setOverrideInterimBase(on);
                              if (on) {
                                setInterimBaseOverrideAmount(
                                  String(interimPreview.baseRentRounded)
                                );
                              }
                            }}
                          />
                          <Label
                            htmlFor="override-interim-base"
                            className="text-xs font-normal cursor-pointer"
                          >
                            Override base amount
                          </Label>
                        </div>

                        {overrideInterimBase && (
                          <div className="relative">
                            <IndianRupee className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input
                              type="number"
                              min={0}
                              step="1"
                              className="pl-10 h-9"
                              value={interimBaseOverrideAmount}
                              onChange={(e) => setInterimBaseOverrideAmount(e.target.value)}
                              placeholder="Enter interim base amount"
                            />
                          </div>
                        )}

                        <div className="flex items-center justify-between text-xs pt-1 border-t">
                          <span className="text-muted-foreground">Final interim base</span>
                          <span className="font-bold text-foreground">
                            ₹{effectiveInterimBase.toLocaleString("en-IN")}
                          </span>
                        </div>
                      </div>
                        )}
                      </>
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        No interim applicable — funding date is on/after first due date.
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Bill To */}
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <Label className="flex items-center gap-2 text-sm font-medium">
                <Building className="h-4 w-4 text-muted-foreground" />
                Bill To{" "}
                <span className="text-xs text-muted-foreground font-normal">(also used as Ship To)</span>
              </Label>
              {customerId && (
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-8 gap-1"
                    onClick={() => setViewAddressOpen(true)}
                  >
                    <Eye className="h-3.5 w-3.5" />
                    View Address
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-8 gap-1"
                    onClick={() => setAddAddressOpen(true)}
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Add Address
                  </Button>
                </div>
              )}
            </div>
            <Select value={billToId} onValueChange={setBillToId}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select billing address">
                  {selectedBillTo
                    ? truncateAddress(selectedBillTo.address, 50)
                    : "Select billing address"}
                </SelectValue>
              </SelectTrigger>
              <SelectContent className="bg-white border max-w-[525px]">
                {gstinAddresses.map((addr, index) => (
                  <SelectItem
                    key={`bill-to-${index}-${addr.id}`}
                    value={addr.id}
                    className="whitespace-normal"
                  >
                    <div className="w-full min-w-0">
                      {addr.label && (
                        <p className="text-xs font-medium text-muted-foreground">{addr.label}</p>
                      )}
                      <span className="font-medium text-sm break-words">{addr.address}</span>
                      {addr.gstin && (
                        <p className="text-xs text-muted-foreground">GSTIN: {addr.gstin}</p>
                      )}
                    </div>
                  </SelectItem>
                ))}
                {gstinAddresses.length === 0 && (
                  <div className="px-2 py-1.5 text-sm text-muted-foreground">
                    No GSTIN addresses available
                  </div>
                )}
              </SelectContent>
            </Select>
            {selectedBillTo && (
              <div className="p-3 bg-muted/50 rounded-md text-sm space-y-1">
                <p className="text-muted-foreground text-xs break-words line-clamp-2">
                  {truncateAddress(selectedBillTo.address, 50)}
                </p>
                {selectedBillTo.gstin && (
                  <p className="text-xs text-muted-foreground">GSTIN: {selectedBillTo.gstin}</p>
                )}
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={!isValid || isSubmitting}>
            <CheckCircle className="h-4 w-4 mr-2" />
            {isSubmitting ? "Approving…" : "Confirm & Approve"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    {customerId && (
      <CustomerAddressManagementDialog
        open={viewAddressOpen}
        add={addAddressOpen}
        onOpenChange={handleAddressDialogChange}
        customerId={customerId}
        onUpdate={handleAddressesUpdated}
      />
    )}
    </>
  );
}
